import { readPairScore } from '@vizit/api';
import type { Retailer } from '@vizit/api';
import * as React from 'react';
import { useCollectionPairScore } from './use-collection-pair-score';

interface UseCustomPairScoreProps {
  workspaceId: string;
  /** The collection the image was opened from, when any */
  collectionId?: string;
  /** The custom benchmark the collection showed with no category, as a picker key; no pair while it stands */
  awaitingCategoryBenchmarkKey?: string;
  imageId: string;
}

/**
 * A collection's selected pair: the listing scores the collection's images under it. Despite the name it is a custom
 * benchmark's or a retailer's, and exactly one of `benchmarkId` and `retailer` is set.
 */
interface CustomPair {
  collectionId: string;
  benchmarkId: string | null;
  retailer: Retailer | null;
  productCategoryId: string;
  /** When the pair's scoring was last requested, by the API's clock */
  requestedAt: string;
}

interface UseCustomPairScoreResult {
  /** The collection's selected pair, a retailer's included, else `null`; what the images are listed and scored under */
  pair: CustomPair | null;
  /** The image's score under `pair` once scored when it is a custom benchmark, which Spark learns from, else `null` */
  score: number | null;
  /** The unrounded score behind `score`, for comparisons that must not round */
  rawScore: number | null;
  /** The image's score under `pair`, a retailer's included, once scored, else `null` */
  collectionScore: number | null;
  /** The unrounded score behind `collectionScore` */
  collectionRawScore: number | null;
  /** The collection or the image's pair score is still loading */
  isLoading: boolean;
  /** The collection has not said yet which pair it selected */
  isPairLoading: boolean;
  /** The collection failed to load, so which pair it selected is unknown */
  isCollectionError: boolean;
  /** The collection showed a custom benchmark with no category under it, so nothing scores the image yet */
  isAwaitingCategory: boolean;
}

/**
 * The image's score under the custom benchmark its collection selected, which Spark learns from in place of the
 * image's own score. A Vizit pair leaves Spark on the image's own score, so it reads as no pair here.
 */
function useCustomPairScore(props: UseCustomPairScoreProps): UseCustomPairScoreResult {
  const { workspaceId, collectionId, awaitingCategoryBenchmarkKey, imageId } = props;

  const { pair, pairScore, awaitingCategoryBenchmark, isCollectionLoading, isCollectionError, isPairScoreLoading } =
    useCollectionPairScore({
      workspaceId,
      collectionId,
      awaitingCategoryBenchmarkKey,
      imageId,
    });

  const retailer = collectionId && pair ? pair.retailer : null;
  const benchmarkId = collectionId && pair && !pair.retailer ? pair.benchmark_id : null;
  const productCategoryId = collectionId && pair ? pair.product_category_id : null;
  const requestedAt = collectionId && pair ? pair.requested_at : null;
  const customPair = React.useMemo(
    () =>
      collectionId && productCategoryId && requestedAt && (retailer || benchmarkId)
        ? { collectionId, benchmarkId: benchmarkId ?? null, retailer: retailer ?? null, productCategoryId, requestedAt }
        : null,
    [collectionId, benchmarkId, retailer, productCategoryId, requestedAt],
  );

  const reading = readPairScore(pairScore);
  const isScored = !!customPair && reading.score !== null;
  // Spark learns from a custom benchmark's score only; a retailer's pair still scores what the grid and header show.
  const isCustomScored = !!customPair?.benchmarkId && isScored;

  return {
    pair: customPair,
    score: isCustomScored ? reading.score : null,
    rawScore: isCustomScored ? (pairScore?.raw_score ?? null) : null,
    collectionScore: isScored ? reading.score : null,
    collectionRawScore: isScored ? (pairScore?.raw_score ?? null) : null,
    isLoading: isCollectionLoading || (!!customPair?.benchmarkId && isPairScoreLoading),
    isPairLoading: isCollectionLoading,
    isCollectionError,
    isAwaitingCategory: !!awaitingCategoryBenchmark,
  };
}

export { useCustomPairScore };
export type { CustomPair, UseCustomPairScoreProps, UseCustomPairScoreResult };
